import React, { useEffect, useState } from "react";
import { Loader, Group, Text, Alert } from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import { useAuth } from "../../context/AuthContext";
import { getApiEndpoint } from "../../config/config";
import { handleGenericError } from "../../utils/errorHandler";

// Renders the custom-deal review/preview. The review document HTML is generated
// server-side: we POST the deal data to /v1/ps/review and inject the HTML it
// returns (inline styles only, no site CSS), matching the generated PDF.
function DealReview(props) {
  const { getVerifiedJWT } = useAuth();
  const [html, setHtml] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Re-fetch whenever the deal data changes.
  const payload = JSON.stringify(props);

  useEffect(() => {
    let cancelled = false;

    const fetchReview = async () => {
      try {
        setLoading(true);
        setError(null);

        const jwt = await getVerifiedJWT();
        if (!jwt) {
          throw new Error("Authentication failed");
        }

        const response = await fetch(getApiEndpoint("/v1/ps/review"), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
          body: payload,
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || `HTTP ${response.status}`);
        }

        // The endpoint may return the HTML as a JSON field or as raw text.
        const contentType = response.headers.get("content-type") || "";
        let reviewHtml = "";
        if (contentType.includes("application/json")) {
          const result = await response.json();
          reviewHtml = result?.data?.html ?? result?.html ?? result?.data ?? "";
        } else {
          reviewHtml = await response.text();
        }

        if (!cancelled) {
          setHtml(typeof reviewHtml === "string" ? reviewHtml : "");
        }
      } catch (err) {
        if (!cancelled) {
          handleGenericError(err, "Failed to generate deal review");
          setError("Failed to generate deal review");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchReview();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payload]);

  if (loading) {
    return (
      <Group justify="center" py="xl">
        <Loader size="sm" />
        <Text size="sm">Generating review...</Text>
      </Group>
    );
  }

  if (error) {
    return (
      <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
        {error}
      </Alert>
    );
  }

  return <div dangerouslySetInnerHTML={{ __html: html }} />;
}

export default DealReview;
